import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import Avatar from '../shared/Avatar';
import { standardBenefitRules } from '../../utils/standardBenefits';
import { isBenefitChangePending } from '../../utils/navCounts';

// ── helpers ───────────────────────────────────────────────────────────────────
function currentBenefitYear() {
  const d = new Date(); const yr = d.getFullYear(); const mo = d.getMonth() + 1;
  return mo >= 3 ? `${yr}-${String(yr+1).slice(-2)}` : `${yr-1}-${String(yr).slice(-2)}`;
}
function shiftBenefitYear(y, by) {
  const start = parseInt(y.split('-')[0]) + by;
  return `${start}-${String(start + 1).slice(-2)}`;
}
// The default points rule, shown to HR beside a person's employment start so an
// override is made knowingly. Mirrors src/lib/benefitPoints.js, which decides.
const POINTS_RULE = 'Employed before 1 March: 4. Joined March to May: 4, June to August: 3, September to November: 2, December to February: 1.';

const BCR_STATUS_COLORS = {
  pending:  'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-600',
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BenefitsAdminPage() {
  const { toast } = useToast();
  const [tab, setTab] = useState('benefits');   // 'benefits' | 'requests' | 'people' | 'standard'
  const [benefits, setBenefits] = useState([]);
  const [changeReqs, setChangeReqs] = useState([]);
  const [reqFilter, setReqFilter]   = useState('pending');
  // The tab's badge is what is waiting, whichever status the list is showing,
  // so it reads the pending requests on their own rather than the list.
  const [pendingCount, setPendingCount] = useState(0);
  const [year, setYear]             = useState(currentBenefitYear());
  const [editBen, setEditBen]       = useState(null);  // benefit being edited
  const [addBen, setAddBen]         = useState(false);
  const [selModal, setSelModal]     = useState(null);  // benefit to show selections for
  const [reviewReq, setReviewReq]   = useState(null);  // BCR to review
  // Staff Allocations: every employee's position for a year, permanent or fixed-term.
  const [allocYear, setAllocYear]   = useState(currentBenefitYear());
  const [allocations, setAllocations] = useState([]);
  const [allocSearch, setAllocSearch] = useState('');
  const [managePerson, setManagePerson] = useState(null); // allocation row being edited
  // Standard benefits: outside the points scheme, every employee who meets the criteria.
  const [standard, setStandard]     = useState([]);
  const [editStd, setEditStd]       = useState(null);  // standard benefit being edited, or {} to add
  const [stdWho, setStdWho]         = useState(null);  // standard benefit to show eligibility for

  const loadBenefits = useCallback(() => {
    api.get('benefits').then(setBenefits).catch(() => setBenefits([]));
  }, []);

  const loadAllocations = useCallback(() => {
    api.get('benefit-allocations', { year: allocYear }).then(setAllocations).catch(() => setAllocations([]));
  }, [allocYear]);
  useEffect(loadAllocations, [loadAllocations]);

  const loadRequests = useCallback(() => {
    api.get('benefit-change-requests', { status: reqFilter }).then(setChangeReqs).catch(() => setChangeReqs([]));
    api.get('benefit-change-requests', { status: 'pending' })
      .then(rows => setPendingCount((Array.isArray(rows) ? rows : []).filter(isBenefitChangePending).length))
      .catch(() => {});
  }, [reqFilter]);

  useEffect(loadBenefits, [loadBenefits]);

  const loadStandard = useCallback(() => {
    api.get('standard-benefits').then(setStandard).catch(() => setStandard([]));
  }, []);
  useEffect(loadStandard, [loadStandard]);

  const toggleStandard = async (b) => {
    await api.put(`standard-benefits/${b.id}`, { is_active: b.is_active ? 0 : 1 }).catch(e => toast.error(e.message));
    loadStandard();
  };
  useEffect(loadRequests, [loadRequests]);

  const toggleBenefit = async (b) => {
    await api.put(`benefits/${b.id}`, { is_active: b.is_active ? 0 : 1 }).catch(e => toast.error(e.message));
    loadBenefits();
  };

  const saveBenefit = async (data) => {
    if (data.id) {
      await api.put(`benefits/${data.id}`, data).catch(e => toast.error(e.message));
    } else {
      await api.post('benefits', data).catch(e => toast.error(e.message));
    }
    setEditBen(null); setAddBen(false); loadBenefits();
  };

  const reviewRequest = async (id, status, reviewer_notes) => {
    await api.put(`benefit-change-requests/${id}`, { status, reviewer_notes }).catch(e => toast.error(e.message));
    setReviewReq(null); loadRequests();
  };


  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Benefits</h2>
          <div className="text-[9px] text-gray-500">
            Flexible benefits: manage the catalogue, review change requests, and assign benefits or set points for individual staff.
            Standard benefits: set up what every employee gets, or comes to get, outside the points scheme.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 mb-3 border-b border-gray-200">
        {[['benefits', 'Flexible Catalogue'], ['requests', 'Change Requests'], ['people', 'Staff Allocations'], ['standard', 'Standard Benefits']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-3 py-1.5 text-[10px] font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${key === 'standard' ? 'ml-4' : ''} ${tab === key ? 'border-gold text-gold' : 'border-transparent text-gray-500 hover:text-navy'}`}>
            {label}
            {key === 'requests' && pendingCount > 0 && (
              <span className="bg-amber-500 text-amber-950 text-[7px] font-bold px-1.5 py-0.5 rounded-full">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── BENEFITS CATALOGUE TAB ── */}
      {tab === 'benefits' && (
        <div>
          <div className="flex justify-end mb-2">
            <button onClick={() => setAddBen(true)}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
              + Add Benefit
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Benefit', 'Description', 'Points', 'Max / year', 'Active', 'Employee Selectable', 'Selections', ''].map(h => (
                    <th key={h} className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {benefits.map(b => (
                  <tr key={b.id} className={`border-b border-gray-100 hover:bg-gold/5 ${!b.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-2 py-1.5">
                      <div className="text-[10px] font-semibold">{b.name}</div>
                      <div className="text-[7px] text-gray-400 font-mono">{b.id}</div>
                    </td>
                    <td className="px-2 py-1.5 max-w-[220px]">
                      <div className="text-[9px] text-gray-500 truncate" title={b.description}>{b.description || '—'}</div>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <span className="text-[11px] font-extrabold text-navy">{b.points_cost}</span>
                      <span className="text-[8px] text-gray-400 ml-0.5">pt{b.points_cost !== 1 ? 's' : ''}</span>
                    </td>
                    <td className="px-2 py-1.5 text-center text-[10px] font-semibold">{b.max_selections}×</td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => toggleBenefit(b)}
                        className={`w-8 h-4 rounded-full transition-colors ${b.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                        <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${b.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {b.is_selectable
                        ? <span className="text-[8px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Yes</span>
                        : <span className="text-[8px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-semibold">HR only</span>
                      }
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => setSelModal(b)}
                        className="text-[8px] px-1.5 py-0.5 bg-navy/10 text-navy font-semibold rounded hover:bg-navy/20">
                        View
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => setEditBen({ ...b })}
                        className="text-[8px] px-1.5 py-0.5 bg-gray-100 text-gray-600 font-semibold rounded hover:bg-gray-200">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {benefits.length === 0 && (
                  <tr><td colSpan="8" className="text-center py-8 text-[10px] text-gray-400">No benefits configured.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CHANGE REQUESTS TAB ── */}
      {tab === 'requests' && (
        <div>
          <div className="flex gap-2 mb-3">
            {['pending','approved','rejected'].map(s => (
              <button key={s} onClick={() => setReqFilter(s)}
                className={`px-2.5 py-1 text-[9px] font-semibold rounded capitalize ${reqFilter === s ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s}
              </button>
            ))}
            <div className="flex items-center gap-1 ml-auto text-[9px] text-gray-500">
              Year:
              <input type="text" className="form-input w-20 text-[9px]" value={year}
                onChange={e => setYear(e.target.value)} placeholder="2026-27" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Employee', 'Year', 'Requested', 'Status', 'Selections', 'Reviewed By', ''].map(h => (
                    <th key={h} className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {changeReqs.map(r => {
                  let selections = [];
                  try { selections = JSON.parse(r.requested_selections || '[]'); } catch {}
                  return (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <Avatar personId={r.person_id} className="w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                            style={{ background: r.avatar_color || '#A1723D' }}>
                            {r.person_name?.charAt(0)}
                          </Avatar>
                          <div>
                            <div className="text-[10px] font-semibold">{r.person_name}</div>
                            {r.designation && <div className="text-[8px] text-gray-400">{r.designation}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[9px] font-semibold text-navy">{r.benefit_year}</td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500">{formatDate(r.requested_at)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${BCR_STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-500'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <SelectionsSummary selectionIds={selections} benefits={benefits} />
                      </td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500">
                        {r.reviewer_name || '—'}
                        {r.reviewer_notes && <div className="text-[8px] text-gray-400 italic">{r.reviewer_notes}</div>}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.status === 'pending' && (
                          <button onClick={() => setReviewReq(r)}
                            className="text-[8px] px-1.5 py-0.5 bg-navy/10 text-navy font-semibold rounded hover:bg-navy/20">
                            Review
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {changeReqs.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-8 text-[10px] text-gray-400">
                    No {reqFilter} requests.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── STAFF ALLOCATIONS TAB ──
          Where HR assigns benefits to a person directly (a company car, or a
          selection on somebody's behalf) and overrides their points for a year.
          The table is every employee's position; Manage opens one. */}
      {tab === 'people' && (() => {
        const q = allocSearch.trim().toLowerCase();
        const rows = allocations.filter(r => !q || r.person_name.toLowerCase().includes(q) || (r.team_name || '').toLowerCase().includes(q));
        const yearOptions = [shiftBenefitYear(currentBenefitYear(), -1), currentBenefitYear(), shiftBenefitYear(currentBenefitYear(), 1)];
        return (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input type="text" className="form-input w-48 text-[9px]" placeholder="Search by name or team"
                value={allocSearch} onChange={e => setAllocSearch(e.target.value)} />
              <div className="flex items-center gap-1 ml-auto text-[9px] text-gray-500">
                Benefit year:
                <select className="form-input w-24 text-[9px]" value={allocYear} onChange={e => setAllocYear(e.target.value)} data-tour="benefits-alloc-year">
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div className="text-[8px] text-gray-500 mb-2">
              Points: {POINTS_RULE} Use Manage to set a person's points where they are an exception, or to assign benefits on their behalf.
            </div>

            <div className="bg-white border border-gray-200 rounded overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {['Employee', 'Started', 'Points', 'Used', 'Left', 'Benefits', 'Status', ''].map(h => (
                      <th key={h} className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.person_id} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <Avatar personId={r.person_id} className="w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                            style={{ background: r.avatar_color || '#A1723D' }}>
                            {r.person_name?.charAt(0)}
                          </Avatar>
                          <div>
                            <div className="text-[10px] font-semibold">{r.person_name}</div>
                            <div className="text-[8px] text-gray-400">{r.team_name || r.designation || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500">{r.start_of_employment ? formatDate(r.start_of_employment) : '—'}</td>
                      <td className="px-2 py-1.5">
                        <span className="text-[11px] font-extrabold text-navy">{r.points_allocated}</span>
                        {r.points_override && (
                          <span className="ml-1 text-[7px] bg-blue-50 text-blue-700 px-1 py-0.5 rounded font-semibold" title={r.points_override.reason || 'Set by HR'}>
                            HR set (default {r.points_default})
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-center">{r.points_used}</td>
                      <td className={`px-2 py-1.5 text-[10px] text-center font-semibold ${r.points_remaining < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{r.points_remaining}</td>
                      <td className="px-2 py-1.5">
                        {r.benefits.length === 0
                          ? <span className="text-[9px] text-gray-300">None</span>
                          : <div className="flex flex-wrap gap-1">
                              {r.benefits.map(b => (
                                <span key={b.benefit_id} className={`text-[7px] px-1 py-0.5 rounded font-semibold ${b.source === 'hr' ? 'bg-blue-50 text-blue-700' : 'bg-navy/10 text-navy'}`}
                                  title={b.source === 'hr' ? 'Assigned by HR' : 'Chosen by the employee'}>
                                  {b.name}
                                </span>
                              ))}
                            </div>}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.locked
                          ? <span className="text-[7px] font-bold px-1.5 py-0.5 rounded uppercase bg-emerald-100 text-emerald-700">Locked</span>
                          : r.request_status === 'pending'
                            ? <span className="text-[7px] font-bold px-1.5 py-0.5 rounded uppercase bg-amber-100 text-amber-700">Awaiting review</span>
                            : <span className="text-[7px] font-bold px-1.5 py-0.5 rounded uppercase bg-gray-100 text-gray-500">Not selected</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button onClick={() => setManagePerson(r)}
                          className="text-[8px] px-1.5 py-0.5 bg-navy/10 text-navy font-semibold rounded hover:bg-navy/20">
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan="8" className="text-center py-8 text-[10px] text-gray-400">
                      {allocations.length === 0 ? 'No employees found.' : 'Nobody matches that search.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── STANDARD BENEFITS TAB ──
          Benefits every employee has, or comes to have, without spending
          points. The criteria are written for people to read; the rules the
          app checks (service, permanent only, passed probation) decide what each person
          is told on My Benefits. Contractors never see them. */}
      {tab === 'standard' && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[8px] text-gray-500 max-w-2xl">
              Shown on My Benefits, apart from the flexible scheme. No points and no request: each employee sees
              only the ones they qualify for today, and a benefit appears for them once they meet its rules.
              Use View to see who qualifies. Retire a benefit to take it off My Benefits without losing it.
            </div>
            <button onClick={() => setEditStd({})}
              className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 shrink-0">
              + Add Standard Benefit
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {['Benefit', 'Who qualifies', 'How to use it', 'Active', 'Eligible', ''].map(h => (
                    <th key={h} className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {standard.map(b => (
                  <tr key={b.id} className={`border-b border-gray-100 hover:bg-gold/5 align-top ${!b.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-2 py-1.5 max-w-[220px]">
                      <div className="text-[10px] font-semibold">{b.name}</div>
                      {b.description && <div className="text-[9px] text-gray-500 line-clamp-2" title={b.description}>{b.description}</div>}
                    </td>
                    <td className="px-2 py-1.5 max-w-[240px]">
                      <span className="text-[8px] bg-navy/10 text-navy px-1.5 py-0.5 rounded font-semibold">{standardBenefitRules(b)}</span>
                      {b.eligibility && <div className="text-[9px] text-gray-500 mt-0.5 line-clamp-2" title={b.eligibility}>{b.eligibility}</div>}
                    </td>
                    <td className="px-2 py-1.5 max-w-[220px]">
                      <div className="text-[9px] text-gray-500 line-clamp-2" title={b.how_to_access || ''}>{b.how_to_access || '—'}</div>
                      {b.link_url && <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="text-[8px] text-blue-700 hover:underline">Link</a>}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => toggleStandard(b)} title={b.is_active ? 'Retire' : 'Restore'}
                        className={`w-8 h-4 rounded-full transition-colors ${b.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                        <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mx-0.5 ${b.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => setStdWho(b)}
                        className="text-[8px] px-1.5 py-0.5 bg-navy/10 text-navy font-semibold rounded hover:bg-navy/20">
                        View
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => setEditStd({ ...b })}
                        className="text-[8px] px-1.5 py-0.5 bg-gray-100 text-gray-600 font-semibold rounded hover:bg-gray-200">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {standard.length === 0 && (
                  <tr><td colSpan="6" className="text-center py-8 text-[10px] text-gray-400">
                    No standard benefits set up yet. Add one and it appears on every employee's My Benefits.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {editStd && (
        <StandardBenefitModal
          benefit={editStd.id ? editStd : null}
          onClose={() => setEditStd(null)}
          onSaved={(saved) => { setEditStd(null); loadStandard(); toast.success(`${saved.name} saved`); }}
        />
      )}

      {stdWho && <StandardEligibilityModal benefit={stdWho} onClose={() => setStdWho(null)} />}

      {(editBen || addBen) && (
        <BenefitEditModal
          benefit={addBen ? null : editBen}
          onClose={() => { setEditBen(null); setAddBen(false); }}
          onSave={saveBenefit}
        />
      )}

      {selModal && (
        <SelectionsModal
          benefit={selModal}
          year={year}
          onClose={() => setSelModal(null)}
        />
      )}

      {reviewReq && (
        <ReviewRequestModal
          request={reviewReq}
          benefits={benefits}
          onClose={() => setReviewReq(null)}
          onSave={reviewRequest}
        />
      )}

      {managePerson && (
        <ManagePersonModal
          row={managePerson}
          year={allocYear}
          benefits={benefits.filter(b => b.is_active)}
          onClose={() => setManagePerson(null)}
          onSaved={() => { setManagePerson(null); loadAllocations(); loadRequests(); }}
        />
      )}
    </div>
  );
}

// ── Inline selections summary ─────────────────────────────────────────────────
function SelectionsSummary({ selectionIds, benefits }) {
  if (!selectionIds || selectionIds.length === 0) return <span className="text-[9px] text-gray-300">None</span>;
  const counts = {};
  for (const id of selectionIds) counts[id] = (counts[id] || 0) + 1;
  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(counts).map(([id, count]) => {
        const b = benefits.find(x => x.id === id);
        return (
          <span key={id} className="text-[7px] bg-navy/10 text-navy px-1 py-0.5 rounded font-semibold">
            {count > 1 ? `${count}× ` : ''}{b?.name || id}
          </span>
        );
      })}
    </div>
  );
}

// ── Benefit edit modal ────────────────────────────────────────────────────────
function BenefitEditModal({ benefit, onClose, onSave }) {
  const [form, setForm] = useState({
    name:           benefit?.name           || '',
    description:    benefit?.description    || '',
    points_cost:    benefit?.points_cost    ?? 1,
    max_selections: benefit?.max_selections ?? 1,
    is_active:      benefit?.is_active      ?? 1,
    is_selectable:  benefit?.is_selectable  ?? 1,
    sort_order:     benefit?.sort_order     ?? 99,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal
      title={benefit ? `Edit: ${benefit.name}` : 'Add New Benefit'}
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button
            onClick={() => onSave({ ...(benefit ? { id: benefit.id } : {}), ...form })}
            disabled={!form.name.trim()}
            className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
          >
            Save
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <textarea className="form-textarea" rows="2" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Points Cost</label>
          <input className="form-input" type="number" min="0" value={form.points_cost} onChange={e => set('points_cost', parseInt(e.target.value) || 0)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Max Selections / Year</label>
          <input className="form-input" type="number" min="1" value={form.max_selections} onChange={e => set('max_selections', parseInt(e.target.value) || 1)} />
          <div className="text-[7px] text-gray-400 mt-0.5">Use 2 for benefits like Extra Annual Leave Day</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sort Order</label>
          <input className="form-input" type="number" min="0" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
        </div>
        <div className="flex gap-4 items-end pb-1">
          <label className="flex items-center gap-1.5 cursor-pointer text-[9px] text-gray-700">
            <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked ? 1 : 0)} className="w-3 h-3" />
            Active
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer text-[9px] text-gray-700">
            <input type="checkbox" checked={!!form.is_selectable} onChange={e => set('is_selectable', e.target.checked ? 1 : 0)} className="w-3 h-3" />
            Employee selectable
          </label>
        </div>
      </div>
    </Modal>
  );
}

// ── Who has this benefit modal ────────────────────────────────────────────────
function SelectionsModal({ benefit, year, onClose }) {
  const [selections, setSelections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`benefits/${benefit.id}/selections`, { year })
      .then(setSelections)
      .catch(() => setSelections([]))
      .finally(() => setLoading(false));
  }, [benefit.id, year]);

  return (
    <Modal title={`Who has: ${benefit.name}`} onClose={onClose}>
      <div className="text-[8px] text-gray-500 mb-2">Benefit year: <strong>{year}</strong></div>
      {loading ? (
        <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div>
      ) : selections.length === 0 ? (
        <div className="text-center py-6 text-[10px] text-gray-400">Nobody has this benefit selected for {year}.</div>
      ) : (
        <div className="space-y-1">
          {selections.map(s => (
            <div key={s.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 border border-gray-100">
              <div className="flex items-center gap-2">
                <Avatar personId={s.person_id} className="w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                  style={{ background: s.avatar_color || '#A1723D' }}>
                  {s.person_name?.charAt(0)}
                </Avatar>
                <div>
                  <div className="text-[10px] font-semibold">{s.person_name}</div>
                  <div className="text-[8px] text-gray-400">{s.team_name || s.designation || '—'}</div>
                </div>
              </div>
              <div className="text-[7px] text-gray-400">{formatDate(s.approved_at || s.selected_at)}</div>
            </div>
          ))}
          <div className="text-[8px] text-gray-400 pt-1">{selections.length} employee{selections.length !== 1 ? 's' : ''}</div>
        </div>
      )}
    </Modal>
  );
}

// ── Review change request modal ───────────────────────────────────────────────
function ReviewRequestModal({ request, benefits, onClose, onSave }) {
  const [notes, setNotes] = useState('');
  let selections = [];
  try { selections = JSON.parse(request.requested_selections || '[]'); } catch {}
  const counts = {};
  for (const id of selections) counts[id] = (counts[id] || 0) + 1;

  // Calculate points total
  const totalPoints = selections.reduce((s, id) => {
    const b = benefits.find(x => x.id === id);
    return s + (b?.points_cost || 0);
  }, 0);

  return (
    <Modal
      title={`Review Request — ${request.person_name}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button
            onClick={() => onSave(request.id, 'rejected', notes)}
            className="px-3 py-1 text-[10px] font-semibold bg-red-500 text-white rounded hover:bg-red-600"
          >
            Reject
          </button>
          <button
            onClick={() => onSave(request.id, 'approved', notes)}
            className="px-3 py-1 text-[10px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700"
          >
            Approve
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[9px] grid grid-cols-2 gap-1">
          <div><span className="font-bold text-gray-500">Employee:</span> {request.person_name}</div>
          <div><span className="font-bold text-gray-500">Benefit Year:</span> {request.benefit_year}</div>
          <div><span className="font-bold text-gray-500">Submitted:</span> {formatDate(request.requested_at)}</div>
          <div><span className="font-bold text-gray-500">Total Points:</span> {totalPoints}</div>
        </div>

        <div>
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">Requested Benefits</div>
          {Object.keys(counts).length === 0 ? (
            <div className="text-[9px] text-gray-400 italic">No benefits selected (requesting removal of all)</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(counts).map(([id, count]) => {
                const b = benefits.find(x => x.id === id);
                return (
                  <div key={id} className="flex items-center justify-between bg-white border border-gray-200 rounded px-2 py-1">
                    <div>
                      <span className="text-[10px] font-semibold">{count > 1 ? `${count}× ` : ''}{b?.name || id}</span>
                      {b?.description && <div className="text-[8px] text-gray-400 mt-0.5 max-w-[320px]">{b.description}</div>}
                    </div>
                    <span className="text-[9px] font-bold text-navy ml-3">{(b?.points_cost || 0) * count}pt{(b?.points_cost || 0) * count !== 1 ? 's' : ''}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selections.some(id => id === 'ben-001') && (
          <div className="text-[9px] bg-blue-50 border border-blue-200 rounded px-2 py-1 text-blue-700">
            ℹ Extra annual leave day(s) included — on approval these will be automatically added to the employee's leave allowances for {request.benefit_year}.
          </div>
        )}

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes (shown to employee)</label>
          <textarea className="form-textarea" rows="2" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional — reason for rejection, confirmation details, etc." />
        </div>
      </div>
    </Modal>
  );
}

// ── Manage one person's year: points override and assigned benefits ─────────
//
// Two things are saved, each only if it changed: the points override (a
// number, or cleared to go back to the rule) and the set of benefits. Saving
// benefits makes the list the whole of the person's year, approved as of now
// and marked as assigned by HR, and supersedes any request they had waiting.
// It also locks the year for them once it includes a flexible benefit.
function ManagePersonModal({ row, year, benefits, onClose, onSaved }) {
  const { toast } = useToast();
  const [overrideOn, setOverrideOn] = useState(!!row.points_override);
  const [points, setPoints]         = useState(row.points_override?.points ?? row.points_default);
  const [reason, setReason]         = useState(row.points_override?.reason || '');
  const [selected, setSelected]     = useState(row.benefits.map(b => b.benefit_id));
  const [touchedBenefits, setTouchedBenefits] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const countOf = (id) => selected.filter(x => x === id).length;
  const add = (id) => {
    const b = benefits.find(x => x.id === id);
    if (!b || countOf(id) >= b.max_selections) return;
    setSelected(p => [...p, id]); setTouchedBenefits(true);
  };
  const remove = (id) => {
    const i = selected.lastIndexOf(id);
    if (i === -1) return;
    setSelected(p => p.filter((_, k) => k !== i)); setTouchedBenefits(true);
  };

  const pointsAllocated = overrideOn ? (Number.isInteger(Number(points)) ? Number(points) : 0) : row.points_default;
  const pointsUsed = selected.reduce((s, id) => s + (benefits.find(b => b.id === id)?.points_cost || 0), 0);
  const remaining = pointsAllocated - pointsUsed;

  const overrideChanged = overrideOn
    ? (Number(points) !== row.points_override?.points || (reason || '') !== (row.points_override?.reason || ''))
    : !!row.points_override;
  const willLock = selected.some(id => benefits.find(b => b.id === id)?.is_selectable);

  const save = async () => {
    setError('');
    if (overrideOn && (!Number.isInteger(Number(points)) || Number(points) < 0 || points === '')) {
      setError('Points must be a whole number, zero or more.'); return;
    }
    const payload = { year };
    if (overrideChanged) {
      payload.points_override = overrideOn ? Number(points) : null;
      payload.override_reason = overrideOn ? reason : null;
    }
    if (touchedBenefits) payload.selections = selected;
    if (!overrideChanged && !touchedBenefits) { onClose(); return; }
    setSaving(true);
    try {
      await api.put(`benefit-allocations/${row.person_id}`, payload);
      toast.success(`${row.person_name}'s ${year} benefits updated`);
      onSaved();
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Manage: ${row.person_name} — ${year}`}
      onClose={onClose}
      wide
      footer={
        <>
          {error && <span className="text-[9px] text-red-600 mr-auto">{error}</span>}
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[9px] grid grid-cols-2 gap-1">
          <div><span className="font-bold text-gray-500">Started:</span> {row.start_of_employment ? formatDate(row.start_of_employment) : 'not recorded'}</div>
          <div><span className="font-bold text-gray-500">Points by the rule:</span> {row.points_default}</div>
          <div><span className="font-bold text-gray-500">Status:</span> {row.locked ? 'Locked (approved)' : row.request_status === 'pending' ? 'Request awaiting review' : 'Nothing selected yet'}</div>
          <div><span className="font-bold text-gray-500">Rule:</span> <span className="text-gray-500">{POINTS_RULE}</span></div>
        </div>

        {/* Points override */}
        <div className="bg-white border border-gray-200 rounded p-2 space-y-1.5" data-tour="benefits-points-override">
          <label className="flex items-center gap-1.5 cursor-pointer text-[9px] text-gray-700 font-semibold">
            <input type="checkbox" className="w-3 h-3" checked={overrideOn}
              onChange={e => { setOverrideOn(e.target.checked); if (e.target.checked && points === '') setPoints(row.points_default); }} />
            Override points for {year}
          </label>
          {overrideOn ? (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Points</label>
                <input className="form-input" type="number" min="0" step="1" value={points}
                  onChange={e => setPoints(e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
              </div>
              <div className="col-span-2">
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Reason (shown to the employee)</label>
                <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Agreed at offer" />
              </div>
            </div>
          ) : (
            <div className="text-[8px] text-gray-400">Using the rule: {row.points_default} point{row.points_default !== 1 ? 's' : ''} from the start of employment.</div>
          )}
        </div>

        {/* Benefits */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[8px] font-bold text-gray-500 uppercase">Benefits for {year}</div>
            <div className={`text-[9px] font-semibold ${remaining < 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {pointsUsed} of {pointsAllocated} points used{remaining < 0 ? ` (${-remaining} over)` : ''}
            </div>
          </div>
          <div className="space-y-1">
            {benefits.map(b => {
              const count = countOf(b.id);
              return (
                <div key={b.id} className={`flex items-center justify-between border rounded px-2 py-1 ${count > 0 ? 'border-gold/40 bg-gold/5' : 'border-gray-200 bg-white'}`}>
                  <div>
                    <span className="text-[10px] font-semibold">{b.name}</span>
                    <span className="text-[8px] font-bold text-navy ml-1.5">{b.points_cost}pt{b.points_cost !== 1 ? 's' : ''}</span>
                    {!b.is_selectable && <span className="text-[7px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded font-semibold ml-1.5">HR only</span>}
                    {b.max_selections > 1 && <span className="text-[7px] text-gray-400 ml-1.5">(max {b.max_selections}×)</span>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {count > 0 && (
                      <button onClick={() => remove(b.id)}
                        className="w-5 h-5 rounded-full bg-red-100 text-red-600 font-bold text-[10px] flex items-center justify-center hover:bg-red-200">−</button>
                    )}
                    {count > 0 && <span className="text-[10px] font-extrabold text-gold w-4 text-center">{count}</span>}
                    <button onClick={() => add(b.id)} disabled={count >= b.max_selections}
                      className="w-5 h-5 rounded-full bg-gold/20 text-gold font-bold text-[10px] flex items-center justify-center hover:bg-gold/40 disabled:opacity-30 disabled:cursor-not-allowed">+</button>
                  </div>
                </div>
              );
            })}
          </div>
          {touchedBenefits && (
            <div className="text-[8px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-2">
              Saving sets {row.person_name}'s {year} benefits to exactly this list, approved now and marked as assigned by HR.
              {row.request_status === 'pending' && ' Their waiting request will be closed as superseded.'}
              {willLock && ' The year will be locked for them: they will not be able to submit a selection of their own.'}
              {selected.some(id => id === 'ben-001') && ' Extra annual leave days will be added to their leave allowance.'}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── Standard benefit: add or edit ────────────────────────────────────────────
// What people read (what it is, who qualifies, how to use it) and the two rules
// the app checks. A failed save stays open with the reason beside the button.
function StandardBenefitModal({ benefit, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:               benefit?.name               || '',
    description:        benefit?.description        || '',
    eligibility:        benefit?.eligibility        || '',
    how_to_access:      benefit?.how_to_access      || '',
    link_url:           benefit?.link_url           || '',
    min_service_months: benefit?.min_service_months ?? 0,
    permanent_only:     benefit?.permanent_only     ?? 0,
    requires_probation: benefit?.requires_probation ?? 0,
    is_active:          benefit?.is_active          ?? 1,
    sort_order:         benefit?.sort_order         ?? 99,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const saved = benefit
        ? await api.put(`standard-benefits/${benefit.id}`, form)
        : await api.post('standard-benefits', form);
      onSaved(saved || form);
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={benefit ? `Edit: ${benefit.name}` : 'Add Standard Benefit'}
      onClose={onClose}
      wide
      footer={
        <>
          {error && <span className="text-[9px] text-red-600 mr-auto">{error}</span>}
          <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          <button onClick={save} disabled={!form.name.trim() || saving}
            className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Employee Assistance Programme" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">What it is</label>
          <textarea className="form-textarea" rows="2" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Who qualifies</label>
          <textarea className="form-textarea" rows="2" value={form.eligibility} onChange={e => set('eligibility', e.target.value)}
            placeholder="The criteria in words, including anything the rules below cannot check" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">How to use it</label>
          <textarea className="form-textarea" rows="2" value={form.how_to_access} onChange={e => set('how_to_access', e.target.value)}
            placeholder="Who to contact, how to claim, or where to sign up" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Link</label>
          <input className="form-input" value={form.link_url} onChange={e => set('link_url', e.target.value)} placeholder="https:// (optional: provider, policy or sign-up page)" />
        </div>

        <div className="col-span-2 border-t border-gray-100 pt-2">
          <div className="text-[9px] font-bold text-gray-600 uppercase mb-1">Rules the app checks</div>
          <div className="text-[8px] text-gray-400 mb-2">
            These decide who sees the benefit on My Benefits: only people who meet them today. Contractors are never included.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Months' service before qualifying</label>
              <input className="form-input" type="number" min="0" step="1" value={form.min_service_months}
                onChange={e => set('min_service_months', e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
              <div className="text-[7px] text-gray-400 mt-0.5">0 for from the first day. Counted from the start of employment.</div>
            </div>
            <div className="flex flex-col justify-end gap-1.5 pb-1">
              <label className="flex items-center gap-1.5 cursor-pointer text-[9px] text-gray-700">
                <input type="checkbox" checked={!!form.permanent_only} onChange={e => set('permanent_only', e.target.checked ? 1 : 0)} className="w-3 h-3" />
                Permanent staff only (not fixed-term)
              </label>
              <div>
                <label className="flex items-center gap-1.5 cursor-pointer text-[9px] text-gray-700">
                  <input type="checkbox" checked={!!form.requires_probation} onChange={e => set('requires_probation', e.target.checked ? 1 : 0)} className="w-3 h-3" />
                  Passed probation
                </label>
                <div className="text-[7px] text-gray-400 mt-0.5 ml-[18px]">
                  Probation marked Passed on the People record. Staff with no probation recorded (no status and no end date) count as passed.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sort Order</label>
          <input className="form-input" type="number" min="0" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-1.5 cursor-pointer text-[9px] text-gray-700">
            <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked ? 1 : 0)} className="w-3 h-3" />
            Active (shown on My Benefits)
          </label>
        </div>
      </div>
    </Modal>
  );
}

// ── Standard benefit: who qualifies ──────────────────────────────────────────
// Every current employee's position under the rules, so HR can see the rules
// do what they meant before anyone reads them on My Benefits.
const STD_STATUS = {
  eligible:     { label: 'Qualifies',     cls: 'bg-emerald-100 text-emerald-700' },
  pending:      { label: 'Not yet',       cls: 'bg-amber-100 text-amber-700' },
  not_eligible: { label: 'Excluded',      cls: 'bg-gray-100 text-gray-500' },
  unknown:      { label: 'No start date', cls: 'bg-red-100 text-red-600' },
};

function StandardEligibilityModal({ benefit, onClose }) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [filter, setFilter]   = useState('all');

  useEffect(() => {
    api.get(`standard-benefits/${benefit.id}/eligibility`)
      .then(setData)
      .catch(e => setError(e.message || 'Could not load'));
  }, [benefit.id]);

  const people = data?.people || [];
  const counts = people.reduce((c, p) => ({ ...c, [p.status]: (c[p.status] || 0) + 1 }), {});
  const shown = filter === 'all' ? people : people.filter(p => p.status === filter);

  return (
    <Modal title={`Who qualifies: ${benefit.name}`} onClose={onClose} wide>
      <div className="text-[8px] text-gray-500 mb-2">Rules: <strong>{standardBenefitRules(benefit)}</strong>. Current employees only.</div>
      {error ? (
        <div className="text-center py-6 text-[10px] text-red-600">{error}</div>
      ) : !data ? (
        <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {[['all', `All ${people.length}`], ...Object.entries(STD_STATUS).filter(([k]) => counts[k]).map(([k, s]) => [k, `${s.label} ${counts[k]}`])].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-2 py-0.5 text-[9px] font-semibold rounded ${filter === k ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {shown.map(p => (
              <div key={p.person_id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 border border-gray-100">
                <div className="flex items-center gap-2">
                  <Avatar personId={p.person_id} className="w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                    style={{ background: p.avatar_color || '#A1723D' }}>
                    {p.person_name?.charAt(0)}
                  </Avatar>
                  <div>
                    <div className="text-[10px] font-semibold">{p.person_name}</div>
                    <div className="text-[8px] text-gray-400">
                      {p.team_name || p.designation || '—'} · started {p.start ? formatDate(p.start) : 'not recorded'}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${STD_STATUS[p.status]?.cls || ''}`}>{STD_STATUS[p.status]?.label || p.status}</span>
                  {p.status === 'pending' && p.from && <div className="text-[8px] text-gray-500 mt-0.5">from {formatDate(p.from)}</div>}
                  {p.status === 'pending' && p.reason && (
                    <div className="text-[8px] text-gray-500 mt-0.5">
                      {p.reason}{p.probation_end && ` Ends ${formatDate(p.probation_end)}.`}
                    </div>
                  )}
                  {p.status === 'eligible' && p.note && <div className="text-[8px] text-gray-400 mt-0.5">{p.note}</div>}
                  {p.status === 'not_eligible' && <div className="text-[8px] text-gray-400 mt-0.5">{p.reason}</div>}
                </div>
              </div>
            ))}
            {shown.length === 0 && <div className="text-center py-6 text-[10px] text-gray-400">Nobody here.</div>}
          </div>
        </>
      )}
    </Modal>
  );
}
